import { socket } from './socket.js';
import { NOTIF_DEFAULTS, chargerPrefsNotif } from '../ui/modals.js';

// --- IndexedDB (stockage blob vidéo de fond — trop lourd pour localStorage) ---
const _IDB_NAME = 'chaos-prefs', _IDB_STORE = 'blobs';
function _idbOpen() {
    return new Promise((res, rej) => {
        const req = indexedDB.open(_IDB_NAME, 1);
        req.onupgradeneeded = e => e.target.result.createObjectStore(_IDB_STORE);
        req.onsuccess = e => res(e.target.result);
        req.onerror = e => rej(e.target.error);
    });
}
function _idbSet(key, val) {
    return _idbOpen().then(db => new Promise((res, rej) => {
        const tx = db.transaction(_IDB_STORE, 'readwrite');
        tx.objectStore(_IDB_STORE).put(val, key);
        tx.oncomplete = () => { db.close(); res(); };
        tx.onerror = e => { db.close(); rej(e.target.error); };
    }));
}
function _idbGet(key) {
    return _idbOpen().then(db => new Promise((res, rej) => {
        const tx = db.transaction(_IDB_STORE, 'readonly');
        const req = tx.objectStore(_IDB_STORE).get(key);
        req.onsuccess = () => { db.close(); res(req.result); };
        req.onerror = e => { db.close(); rej(e.target.error); };
    }));
}
function _idbDel(key) {
    return _idbOpen().then(db => new Promise((res, rej) => {
        const tx = db.transaction(_IDB_STORE, 'readwrite');
        tx.objectStore(_IDB_STORE).delete(key);
        tx.oncomplete = () => { db.close(); res(); };
        tx.onerror = e => { db.close(); rej(e.target.error); };
    }));
}
let _bgVideoBlobUrl = null;

export function hexToRgb(hex) {
    hex = hex.replace(/^#/, '');
    let bigint = parseInt(hex, 16);
    let r = (bigint >> 16) & 255;
    let g = (bigint >> 8) & 255;
    let b = bigint & 255;
    return `${r}, ${g}, ${b}`;
}

export function _hexVersRgb(hex) {
    hex = String(hex).replace(/^#/, '');
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    const n = parseInt(hex, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function _rgbVersHex(r, g, b) {
    const h = v => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0');
    return '#' + h(r) + h(g) + h(b);
}

export function _luminance({ r, g, b }) {
    const lin = c => { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

export function _contraste(c1, c2) {
    const L1 = _luminance(c1), L2 = _luminance(c2);
    return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
}

export function _rgbVersHsl({ r, g, b }) {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s; const l = (max + min) / 2;
    if (max === min) { h = s = 0; }
    else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            default:  h = (r - g) / d + 4;
        }
        h /= 6;
    }
    return { h, s, l };
}

export function _hslVersRgb({ h, s, l }) {
    if (s === 0) { const v = l * 255; return { r: v, g: v, b: v }; }
    const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1; if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    return {
        r: hue2rgb(p, q, h + 1 / 3) * 255,
        g: hue2rgb(p, q, h) * 255,
        b: hue2rgb(p, q, h - 1 / 3) * 255
    };
}

export function _couleurLisibleSurFond(hex, fondHex, ratioMin) {
    const fond = _hexVersRgb(fondHex);
    const rgb = _hexVersRgb(hex);
    if (_contraste(rgb, fond) >= ratioMin) return hex;
    const hsl = _rgbVersHsl(rgb);
    for (let l = hsl.l; l <= 0.92; l += 0.02) {
        const test = _hslVersRgb({ h: hsl.h, s: hsl.s, l });
        if (_contraste(test, fond) >= ratioMin) return _rgbVersHex(test.r, test.g, test.b);
    }
    const cl = _hslVersRgb({ h: hsl.h, s: hsl.s, l: 0.92 });
    return _rgbVersHex(cl.r, cl.g, cl.b);
}

export function appliquerTheme(couleurHexa) {
    const root = document.documentElement;
    const lisible = _couleurLisibleSurFond(couleurHexa, '#0d1117', 4.5);
    root.style.setProperty('--theme-primary', lisible);
    root.style.setProperty('--theme-primary-rgb', hexToRgb(couleurHexa));
    localStorage.setItem('chaos_theme_color', couleurHexa);
    if (typeof window._pousserPrefs === 'function') window._pousserPrefs({ theme_color: couleurHexa });
    const picker = document.getElementById('theme-color-picker');
    if (picker && picker.value !== couleurHexa) picker.value = couleurHexa;
    const sw = document.getElementById('swatch-theme');
    if (sw) sw.style.background = couleurHexa;
}

export function selectionnerVideoFond(nomFichier) {
    if (nomFichier === 'custom-image') {
        document.getElementById('bg-custom-input')?.click();
        return;
    }
    localStorage.removeItem('chaos_bg_custom');
    localStorage.removeItem('chaos_bg_type');
    _idbDel('chaos_bg_custom_media').catch(() => {});
    _idbDel('chaos_bg_custom_video').catch(() => {}); // nettoyage ancienne clé
    if (_bgVideoBlobUrl) { URL.revokeObjectURL(_bgVideoBlobUrl); _bgVideoBlobUrl = null; }
    _effacerFondCustomDOM();
    changerVideoFond(nomFichier);
}

export function changerVideoFond(nomFichier) {
    if (localStorage.getItem('chaos_bg_custom')) return;
    if (localStorage.getItem('chaos_bg_type') === 'custom') return;
    const videoEl = document.getElementById('bg-video-chaos');
    if (!videoEl) return;
    const sourceEl = videoEl.querySelector('source');

    _effacerFondCustomDOM();

    localStorage.setItem('chaos_bg_video', nomFichier);
    if (typeof window._pousserPrefs === 'function') window._pousserPrefs({ bg_video: nomFichier });

    if (nomFichier === 'none') {
        videoEl.style.display = 'none';
        return;
    }

    videoEl.style.display = 'block';
    if (sourceEl) sourceEl.src = `videos/${nomFichier}`;
    videoEl.load();
}

function _effacerFondCustomDOM() {
    const div = document.getElementById('bg-custom-chaos');
    if (div) { div.style.backgroundImage = ''; div.style.display = 'none'; }
    const btnRetirer = document.getElementById('btn-remove-custom-bg');
    if (btnRetirer) btnRetirer.style.display = 'none';
}

export function supprimerFondPersonnalise() {
    localStorage.removeItem('chaos_bg_custom');
    localStorage.removeItem('chaos_bg_type');
    _idbDel('chaos_bg_custom_media').catch(() => {});
    _idbDel('chaos_bg_custom_video').catch(() => {}); // nettoyage ancienne clé
    if (_bgVideoBlobUrl) { URL.revokeObjectURL(_bgVideoBlobUrl); _bgVideoBlobUrl = null; }
    _effacerFondCustomDOM();
    const videoNom = localStorage.getItem('chaos_bg_video') || 'fond1.mp4';
    const sel = document.getElementById('bg-video-select');
    if (sel) sel.value = videoNom;
    changerVideoFond(videoNom);
}

function _appliquerVideoFond(url, mimeType) {
    const videoEl = document.getElementById('bg-video-chaos');
    if (!videoEl) return;
    const sourceEl = videoEl.querySelector('source');
    videoEl.style.display = 'block';
    if (sourceEl) { sourceEl.src = url; sourceEl.type = mimeType || 'video/mp4'; }
    videoEl.load();
}

function _appliquerImageFond(url) {
    const div = document.getElementById('bg-custom-chaos');
    if (!div) return;
    const videoEl = document.getElementById('bg-video-chaos');
    if (videoEl) videoEl.style.display = 'none';
    div.style.backgroundImage = `url('${url}')`;
    div.style.display = 'block';
}

function _appliquerCustomMedia(blob) {
    if (_bgVideoBlobUrl) { URL.revokeObjectURL(_bgVideoBlobUrl); _bgVideoBlobUrl = null; }
    const url = URL.createObjectURL(blob);
    _bgVideoBlobUrl = url;
    const div = document.getElementById('bg-custom-chaos');
    if (blob.type.startsWith('video/')) {
        if (div) { div.style.backgroundImage = ''; div.style.display = 'none'; }
        _appliquerVideoFond(url, blob.type);
    } else {
        _appliquerImageFond(url);
    }
    const sel = document.getElementById('bg-video-select');
    if (sel) sel.value = 'custom-image';
    const btnRetirer = document.getElementById('btn-remove-custom-bg');
    if (btnRetirer) btnRetirer.style.display = 'inline-flex';
}

export async function appliquerFondPersonnalise(input) {
    const file = input?.files?.[0];
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) {
        if (typeof window.popToast === 'function') window.popToast('Fichier trop lourd (max 30 Mo).', 'error');
        input.value = '';
        return;
    }
    try {
        await _idbSet('chaos_bg_custom_media', file);
        localStorage.removeItem('chaos_bg_custom');
        localStorage.setItem('chaos_bg_type', 'custom');
        _appliquerCustomMedia(file);
        if (typeof window.popToast === 'function') window.popToast('Fond personnalisé appliqué !', 'success');
    } catch {
        if (typeof window.popToast === 'function') window.popToast('Erreur lors du stockage du fond.', 'error');
    }
    input.value = '';
}

export async function restaurerFondCustomSiPresent() {
    const bgType = localStorage.getItem('chaos_bg_type');
    // Nouveau format unifié (IDB chaos_bg_custom_media)
    if (bgType === 'custom') {
        try {
            const blob = await _idbGet('chaos_bg_custom_media');
            if (blob) { _appliquerCustomMedia(blob); return; }
            localStorage.removeItem('chaos_bg_type');
        } catch {}
    }
    // Migration : ancienne clé vidéo (session précédente)
    if (bgType === 'custom-video') {
        try {
            const blob = await _idbGet('chaos_bg_custom_video');
            if (blob) {
                await _idbSet('chaos_bg_custom_media', blob);
                localStorage.setItem('chaos_bg_type', 'custom');
                _idbDel('chaos_bg_custom_video').catch(() => {});
                _appliquerCustomMedia(blob);
                return;
            }
            localStorage.removeItem('chaos_bg_type');
        } catch {}
    }
    // Legacy : dataURL en localStorage
    const dataUrl = localStorage.getItem('chaos_bg_custom');
    if (!dataUrl) return;
    _appliquerImageFond(dataUrl);
    const sel = document.getElementById('bg-video-select');
    if (sel) sel.value = 'custom-image';
    const btnRetirer = document.getElementById('btn-remove-custom-bg');
    if (btnRetirer) btnRetirer.style.display = 'inline-flex';
}

window._applyingPrefs = true;
export let _prefsPending = {};
export let _prefsTimer = null;

export function _flushPrefs() {
    clearTimeout(_prefsTimer);
    _prefsTimer = null;
    const p = _prefsPending; _prefsPending = {};
    if (Object.keys(p).length && socket && socket.connected) {
        socket.emit('maj-preferences', p);
    }
}

export function _pousserPrefs(patch) {
    if (window._applyingPrefs || !patch) return;
    Object.assign(_prefsPending, patch);
    clearTimeout(_prefsTimer);
    _prefsTimer = setTimeout(_flushPrefs, 700);
}

export function _collectePrefsLocales() {
    const p = {};
    const loc = localStorage.getItem('chaos_locale'); if (loc) p.locale = loc;
    const th = localStorage.getItem('chaos_theme_color'); if (th) p.theme_color = th;
    const bg = localStorage.getItem('chaos_bg_video'); if (bg) p.bg_video = bg;
    try {
        const np = JSON.parse(localStorage.getItem('chaos_notif_prefs') || 'null');
        if (np && typeof np === 'object') p.notif_prefs = np;
    } catch (_) {}
    try {
        const sf = JSON.parse(localStorage.getItem('sphere-folders') || 'null');
        if (sf && typeof sf === 'object') p.sphere_folders = sf;
    } catch (_) {}
    try {
        const so = JSON.parse(localStorage.getItem('ordre-spheres') || 'null');
        if (Array.isArray(so)) p.sphere_order = so;
    } catch (_) {}
    return p;
}

export function chargerPreferencesServeur() {
    socket.emit('get-preferences');
    setTimeout(() => { window._applyingPrefs = false; }, 10000);
}

export async function _appliquerPrefsServeur(p) {
    window._applyingPrefs = true;
    try {
        if (p.theme_color) appliquerTheme(p.theme_color);
        if (p.bg_video && !localStorage.getItem('chaos_bg_custom')) {
            const sel = document.getElementById('bg-video-select');
            if (sel) sel.value = p.bg_video;
            changerVideoFond(p.bg_video);
        }
        if (p.notif_prefs && typeof p.notif_prefs === 'object') {
            const merged = Object.assign({}, NOTIF_DEFAULTS, p.notif_prefs);
            localStorage.setItem('chaos_notif_prefs', JSON.stringify(merged));
            if (typeof chargerPrefsNotif === 'function') chargerPrefsNotif();
        }
        let _spheresAReRendre = false;
        if (p.sphere_folders && typeof p.sphere_folders === 'object'
            && !Array.isArray(p.sphere_folders) && Object.keys(p.sphere_folders).length > 0) {
            localStorage.setItem('sphere-folders', JSON.stringify(p.sphere_folders));
            _spheresAReRendre = true;
        }
        if (Array.isArray(p.sphere_order) && p.sphere_order.length > 0) {
            localStorage.setItem('ordre-spheres', JSON.stringify(p.sphere_order));
            _spheresAReRendre = true;
        }
        if (_spheresAReRendre && window.mesSpheresActuelles && window.mesSpheresActuelles.length) {
            if (typeof window.rendreListeSpheres === 'function') window.rendreListeSpheres(window.mesSpheresActuelles);
        }
        if (p.locale && p.locale !== (localStorage.getItem('chaos_locale') || 'fr')) {
            if (typeof window.changerLangue === 'function') { try { await window.changerLangue(p.locale); } catch (_) {} }
        } else if (p.locale) {
            localStorage.setItem('chaos_locale', p.locale);
        }
        if (typeof p.effets_message === 'boolean') {
            if (typeof window._appliquerEffetsMessage === 'function') window._appliquerEffetsMessage(p.effets_message);
            const toggle = document.getElementById('toggle-effets-message');
            if (toggle) toggle.checked = p.effets_message;
        }
    } finally {
        window._applyingPrefs = false;
    }
}

export function initPrefsSync() {
    window.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') _flushPrefs();
    });
    window.addEventListener('pagehide', _flushPrefs);
    window.addEventListener('beforeunload', _flushPrefs);

    socket.on('mes-preferences', (p) => {
        if (!p || typeof p !== 'object' || Array.isArray(p)) p = {};
        if (Object.keys(p).length === 0) {
            const snap = _collectePrefsLocales();
            if (Object.keys(snap).length) socket.emit('maj-preferences', snap);
            window._applyingPrefs = false; 
            return;
        }
        _appliquerPrefsServeur(p); 
    });
}